import React from "react";
import { decideTargetForFilename } from "../utils/uploadRouter";
import { formatScopedKey, buildFirmexFolderLabel } from "../utils/emailHelper";
import type { VDRProviderId, IndexRow } from "../vdr/providers";
import { FDD_TOOLS, type AnalysisType } from "../fdd/types";
import { addToQueue, getFDDQueue } from "../fdd/store/fddStore";
import { useIrlMatches } from "../irl/match/useIrlMatches";
import { isCodeExcluded } from "../irl/match/folderTree";
import type { MatchResult } from "../irl/match/types";
import { getIRLMatchExcludedFolders, getIRLRequests } from "../irl/store/irlStore";

type FilePreviewRow = {
  file: File;
  selected: boolean;
  targetFolder: string | null;
  error?: string;
};

type FilePreviewTableProps = {
  files: File[];
  selectedFiles: Set<string>; // Set of file names
  onToggleFile: (fileName: string) => void;
  onToggleAll: () => void;
  onDeselectFiles: (fileNames: string[]) => void;
  rootItemId: string;
  indexMap: Record<string, string>;
  providerId?: VDRProviderId;
  indexRows?: IndexRow[];
  projectId?: string;
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function renderIrlCell(
  match: MatchResult | undefined,
  irlListMap: Map<string, { topic?: string; request: string }>,
  classifying: boolean,
  isExcluded: boolean,
): React.ReactNode {
  // Render-time exclusion takes precedence over any cached match — toggling
  // the IRL tab's folder picker should flip the cell immediately on tab
  // return without forcing a re-run of the matcher.
  if (isExcluded) {
    return (
      <span
        title="This file's top-level folder is excluded in the IRL tab. Re-enable it there to include this file in the next match run."
        style={{ fontSize: 11, color: "#9ca3af", cursor: "help", fontStyle: "italic" }}
      >
        — (folder excluded)
      </span>
    );
  }

  if (!match) {
    return classifying ? (
      <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>matching…</span>
    ) : (
      <span style={{ fontSize: 11, color: "#9ca3af" }}>—</span>
    );
  }

  if (match.irlNumber) {
    const meta = irlListMap.get(match.irlNumber);
    const topicLine = meta?.topic ? `Topic: ${meta.topic}\n` : "";
    const tooltip = `IRL ${match.irlNumber}\n${topicLine}${meta?.request ?? ""}\n\n${match.reasoning}\n\nConfidence: ${(match.confidence * 100).toFixed(0)}%`;
    return (
      <span
        title={tooltip}
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: 6,
          background: "#dff3d9",
          color: "#15803d",
          fontWeight: 600,
          fontSize: 12,
          fontFamily: "monospace",
          cursor: "help",
        }}
      >
        {match.irlNumber}
      </span>
    );
  }

  if (match.reasoning === "folder excluded") {
    return (
      <span
        title="This file's top-level folder is excluded in the IRL tab. Re-enable it there to include this file in the next match run."
        style={{ fontSize: 11, color: "#9ca3af", cursor: "help", fontStyle: "italic" }}
      >
        — (folder excluded)
      </span>
    );
  }

  // No match cleared the floor — show alternatives in the hover, render "—"
  if (match.alternatives.length > 0) {
    const altsLine = match.alternatives
      .map((a) => `${a.number} (${(a.confidence * 100).toFixed(0)}%)`)
      .join(", ");
    const tooltip = `Low confidence — top candidates: ${altsLine}\n\n${match.reasoning}`;
    return (
      <span
        title={tooltip}
        style={{
          fontSize: 11,
          color: "#b7950b",
          cursor: "help",
          fontStyle: "italic",
        }}
      >
        — (low conf.)
      </span>
    );
  }

  return (
    <span title={match.reasoning} style={{ fontSize: 11, color: "#9ca3af", cursor: "help" }}>
      —
    </span>
  );
}

type FileRoute = {
  name: string;
  label: string;
  /** Full parent code (e.g., "1.2.3") — null when the file lands at the root. */
  parentCode: string | null;
  hasError: boolean;
};

function computeRoute(
  file: File,
  rootItemId: string,
  indexMap: Record<string, string>,
  providerId?: VDRProviderId,
  indexRows?: IndexRow[],
): FileRoute {
  const decision = decideTargetForFilename(file.name, rootItemId, indexMap, providerId, indexRows, file);
  if ("error" in decision) {
    return { name: file.name, label: `❌ ${decision.error}`, parentCode: null, hasError: true };
  }
  // One file, several folders (request-list VDRs)
  if (decision.allTargets) {
    const [first, ...rest] = decision.allTargets;
    return {
      name: file.name,
      label: `📁 ${first.label}${rest.length ? ` (+${rest.length} more)` : ""}`,
      parentCode: decision.parentCode ?? null,
      hasError: false,
    };
  }
  return {
    name: file.name,
    label: !decision.parentCode
      ? "📁 Root"
      : providerId === "firmex" && indexRows?.length
      ? `📁 ${buildFirmexFolderLabel(decision.parentCode, indexRows)}`
      : `📁 ${formatScopedKey(decision.parentCode)}`,
    parentCode: decision.parentCode ?? null,
    hasError: false,
  };
}

export default function FilePreviewTable({
  files,
  selectedFiles,
  onToggleFile,
  onToggleAll,
  onDeselectFiles,
  rootItemId,
  indexMap,
  providerId,
  indexRows,
  projectId,
}: FilePreviewTableProps) {
  const [queueVersion, setQueueVersion] = React.useState(0);

  // Look up which FDD tools each file (by name) is currently queued for.
  // The FDD store is in-memory only (no localStorage), so this naturally
  // reflects only what the user picked during the current session.
  const queuedToolsByFile = React.useMemo<Record<string, AnalysisType[]>>(() => {
    if (!projectId) return {};
    const map: Record<string, AnalysisType[]> = {};
    for (const item of getFDDQueue(projectId)) {
      const list = map[item.fileName] || (map[item.fileName] = []);
      if (!list.includes(item.analysisType)) list.push(item.analysisType);
    }
    return map;
  }, [projectId, queueVersion]);

  const handleQueueChange = (file: File, analysisType: AnalysisType | "") => {
    if (!analysisType || !projectId) return;
    if (queuedToolsByFile[file.name]?.includes(analysisType)) return; // already queued — no-op
    // The file isn't in SharePoint yet — record placeholder refs and mark as
    // pending_upload. The VDR upload handler stamps real driveId/fileId once
    // the upload completes; on failure it flips this to "error".
    addToQueue(projectId, {
      fileId: "",
      fileName: file.name,
      fileWebUrl: "",
      fileSize: file.size,
      source: "sharepoint",
      analysisType,
      projectId,
      status: "pending_upload",
    });
    setQueueVersion((v) => v + 1);
  };
  const allSelected = files.length > 0 && files.every(f => selectedFiles.has(f.name));
  const someSelected = files.some(f => selectedFiles.has(f.name));

  // Resolve every file's destination route ONCE per render (or per dep change).
  // `decideTargetForFilename` logs unconditionally, so calling it 3+ times per
  // file (label, root filter, IRL folder code) was producing ugly log spam.
  const fileRoutes = React.useMemo<FileRoute[]>(
    () => files.map((f) => computeRoute(f, rootItemId, indexMap, providerId, indexRows)),
    [files, rootItemId, indexMap, providerId, indexRows],
  );

  // Per-file full parent code (e.g., "1.2.3"). The IRL matcher walks ancestors
  // from this against the project's excluded folder set.
  const folderCodes = React.useMemo<(string | null)[]>(
    () => fileRoutes.map((r) => r.parentCode),
    [fileRoutes],
  );

  // Snapshot of the project's excluded folder list for render-time gating.
  // Re-read on every mount of FilePreviewTable so toggling in the IRL tab
  // takes effect when the user comes back to VDR.
  const excludedFolders = React.useMemo<Set<string>>(
    () => (projectId ? new Set(getIRLMatchExcludedFolders(projectId)) : new Set()),
    [projectId],
  );

  // IRL matching: manual-trigger via "Run IRL match" button. The hook hydrates
  // from the cache on mount/file-change so previously-classified files surface
  // their match without an API call.
  const irl = useIrlMatches({ projectId, files, folderCodes });
  const irlListMap = React.useMemo(() => {
    if (!projectId) return new Map<string, { topic?: string; request: string }>();
    const list = getIRLRequests(projectId);
    return new Map(list.map((r) => [r.number, { topic: r.topic, request: r.request }]));
  }, [projectId, irl.matches]);
  const matchedCount = Object.values(irl.matches).filter((m) => !!m.irlNumber).length;

  const rootFiles = fileRoutes.filter((r) => r.label === "📁 Root").map((r) => r.name);
  const rootSelectedCount = rootFiles.filter((n) => selectedFiles.has(n)).length;

  // Files that failed to route (❌) — offered as a one-click deselect so the batch
  // can proceed without them; uses the same deselect mechanism as manual deselect.
  const unroutedFiles = fileRoutes.filter((r) => r.hasError).map((r) => r.name);
  const unroutedSelectedCount = unroutedFiles.filter((n) => selectedFiles.has(n)).length;

  return (
    <div
      style={{
        marginTop: 16,
        border: "1px solid #c1e2b3",
        borderRadius: 10,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#eaf7e6",
          padding: "12px 16px",
          borderBottom: "1px solid #c1e2b3",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontWeight: 700, color: "#2f6b2f" }}>
          File Preview ({files.length} file{files.length !== 1 ? 's' : ''})
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {unroutedSelectedCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeselectFiles(unroutedFiles.filter(n => selectedFiles.has(n)));
              }}
              style={{
                background: "#dc2626",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Deselect Unrouted ({unroutedSelectedCount})
            </button>
          )}
          {rootSelectedCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeselectFiles(rootFiles.filter(n => selectedFiles.has(n)));
              }}
              style={{
                background: "#e08e0b",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Deselect Root ({rootSelectedCount})
            </button>
          )}
          {projectId && irl.hasIrlList && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                void irl.classify();
              }}
              disabled={irl.classifying || files.length === 0}
              title="Use Claude to match each file to an IRL request. Cached results show automatically."
              style={{
                background: irl.classifying ? "#9ca3af" : "#2f6b2f",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                cursor: irl.classifying || files.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {irl.classifying
                ? "Matching IRL…"
                : matchedCount > 0
                  ? `Re-run IRL match (${matchedCount} matched)`
                  : "Run IRL match"}
            </button>
          )}
          <span style={{ fontSize: 12, color: "#4a7d4a" }}>
            Click rows to exclude files from upload
          </span>
        </div>
      </div>

      {projectId && !irl.hasIrlList && (
        <div
          style={{
            background: "#fffbeb",
            borderBottom: "1px solid #f5d97e",
            padding: "8px 16px",
            fontSize: 12,
            color: "#92651e",
          }}
        >
          ⓘ No IRL list uploaded for this project. Upload one in the IRL tab to enable per-file matching.
        </div>
      )}

      {irl.error && (
        <div
          style={{
            background: "#fef2f2",
            borderBottom: "1px solid #fca5a5",
            padding: "8px 16px",
            fontSize: 12,
            color: "#dc2626",
          }}
        >
          ❌ {irl.error}
        </div>
      )}

      {/* Table */}
      <div style={{ overflow: "auto", maxHeight: 400 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                background: "#f4fbf1",
                borderBottom: "1px solid #c1e2b3",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#2f6b2f",
                  width: 40,
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected && !allSelected;
                    }
                  }}
                  onChange={onToggleAll}
                  style={{ cursor: "pointer" }}
                />
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#2f6b2f",
                }}
              >
                File Name
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#2f6b2f",
                  width: 200,
                }}
              >
                Target Folder
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "right",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#2f6b2f",
                  width: 100,
                }}
              >
                Size
              </th>
              {projectId && irl.hasIrlList && (
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#2f6b2f",
                    width: 90,
                  }}
                >
                  IRL
                </th>
              )}
              {projectId && (
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#2f6b2f",
                    width: 180,
                  }}
                >
                  FDD Queue
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {files.map((file, fileIdx) => {
              const route = fileRoutes[fileIdx];
              const isSelected = selectedFiles.has(file.name);
              const folderLabel = route.label;
              const hasError = route.hasError;
              const isFolderExcluded = isCodeExcluded(route.parentCode, excludedFolders);

              return (
                <tr
                  key={file.name}
                  onClick={() => onToggleFile(file.name)}
                  style={{
                    borderBottom: "1px solid #e5f2e0",
                    cursor: "pointer",
                    background: isSelected ? "#fff" : "#f9f9f9",
                    opacity: isSelected ? 1 : 0.6,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isSelected ? "#f4fbf1" : "#f0f0f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected ? "#fff" : "#f9f9f9";
                  }}
                >
                  <td style={{ padding: "8px 12px", textAlign: "left" }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleFile(file.name)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 13,
                      color: "#333",
                      fontFamily: "monospace",
                    }}
                  >
                    {file.name}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 12,
                      color: hasError ? "#d32f2f" : "#4a7d4a",
                      fontWeight: hasError ? 600 : 400,
                    }}
                  >
                    {folderLabel}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: 12,
                      color: "#666",
                      textAlign: "right",
                    }}
                  >
                    {formatFileSize(file.size)}
                  </td>
                  {projectId && irl.hasIrlList && (
                    <td
                      style={{ padding: "8px 12px", textAlign: "center" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderIrlCell(irl.matches[file.name], irlListMap, irl.classifying, isFolderExcluded)}
                    </td>
                  )}
                  {projectId && (
                    <td
                      style={{ padding: "8px 12px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(() => {
                        const queued = queuedToolsByFile[file.name] || [];
                        const hasQueued = queued.length > 0;
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <select
                              value=""
                              onChange={(e) => handleQueueChange(file, e.target.value as AnalysisType | "")}
                              title="Queue this file for FDD analysis"
                              style={{
                                padding: "6px 8px",
                                borderRadius: 6,
                                border: `1px solid ${hasQueued ? "#6aa84f" : "#cfe6c7"}`,
                                background: hasQueued ? "#dff3d9" : "#ffffff",
                                color: "#2f6b2f",
                                fontSize: 12,
                                fontWeight: hasQueued ? 600 : 400,
                                cursor: "pointer",
                                width: "100%",
                              }}
                            >
                              <option value="">
                                {hasQueued
                                  ? `✓ Queued (${queued.length}) — add another…`
                                  : "Queue for…"}
                              </option>
                              {FDD_TOOLS.map((t) => {
                                const already = queued.includes(t.id);
                                return (
                                  <option key={t.id} value={t.id} disabled={already}>
                                    {already ? "✓ " : ""}{t.icon} {t.name}{already ? " (queued)" : ""}
                                  </option>
                                );
                              })}
                            </select>
                            {hasQueued && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {queued.map((toolId) => {
                                  const t = FDD_TOOLS.find((x) => x.id === toolId);
                                  if (!t) return null;
                                  return (
                                    <span
                                      key={toolId}
                                      title={`Queued for ${t.name}`}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 3,
                                        padding: "1px 6px",
                                        background: "#6aa84f",
                                        color: "#fff",
                                        borderRadius: 4,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {t.icon} {t.name}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Summary */}
      <div
        style={{
          background: "#f4fbf1",
          padding: "10px 16px",
          borderTop: "1px solid #c1e2b3",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 12,
          color: "#2f6b2f",
        }}
      >
        <div>
          <strong>{selectedFiles.size}</strong> of <strong>{files.length}</strong> selected for upload
        </div>
        {selectedFiles.size < files.length && (
          <div style={{ color: "#d68910", fontWeight: 600 }}>
            {files.length - selectedFiles.size} file{files.length - selectedFiles.size !== 1 ? 's' : ''} will be excluded
          </div>
        )}
      </div>
    </div>
  );
}
